import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Action, Selector, State, StateContext, Store } from "@ngxs/store";
import { map } from "rxjs/operators";
import { DashboardModel } from "src/app/shared/interfaces/dashboard";

import { GET_REDIRECT_URL } from "../customer-management/customer-management.action";
import {
  GET_ANNOUNCEMENTS,
  GET_CAROUSEL_CONTENT,
  GET_CHILDSUBMENU_REDIRECTURL,
  GET_MENU_LIST,
  GET_OUTSTANDING_CB,
  GET_POLICY_MANAGEMENT,
  GET_POLICY_STATS,
  GET_POSTCODE_INFO,
  GET_SUBMENU_REDIRECTURL,
  GET_TOTAL_OUTSTANDING,
  GET_USER_PERFORMANCE,
  REDIRECT_PROFILE,
  RESET_DISABLED_LOADING_STATE,
  SAVE_QUICKLINK,
  SEND_QL_DATA,
} from "./landing-page.action";
import { LandingPageService } from "./landing-page.service";

@State<DashboardModel>({
  name: "LandingPageState",
  defaults: {
    liferayContent: null,
    carouselContent: null,
    postCodeInfo: {
      code: "",
      descp: "",
      city: "",
      state: "",
    },
    policyStats: null,
    dashboardMenu: [],
    qlData: null,
    readNotif: null,
    announcementList: {
      disabledLoading: false,
      data: null,
    },
    userPerformance: {
      disabledLoading: false,
      data: null,
    },
    totalOutstanding: {
      // disabledLoading: false,
      data: null,
    },
    outstandingCb: {
      // disabledLoading: false,
      data: null,
    },
    policyManagement: {
      disabledLoading: false,
      data: null,
    },
    disabledLoading: false,
  },
})
@Injectable()
export class LandingPageState {
  constructor(
    private readonly landingPageService: LandingPageService,
    private readonly router: Router,
    private readonly store: Store,
  ) {}

  @Selector()
  public static policyManagement(state: DashboardModel) {
    return state.policyManagement;
  }

  @Selector()
  public static userPerformance(state: DashboardModel) {
    return state.userPerformance;
  }

  @Selector()
  public static totalOutstanding(state: DashboardModel) {
    return state.totalOutstanding;
  }

  @Selector()
  public static outstandingCb(state: DashboardModel) {
    return state.outstandingCb;
  }

  @Selector()
  public static getLifeRayContent(state: DashboardModel) {
    return state.liferayContent ? state.liferayContent : null;
  }

  @Selector()
  public static carouselContent(state: DashboardModel) {
    return state.carouselContent ? state.carouselContent : [];
  }

  @Selector()
  public static announcementList(state: DashboardModel) {
    return state.announcementList ? state.announcementList : null;
  }

  @Selector()
  public static disabledLoading(state: DashboardModel) {
    return state.disabledLoading;
  }

  @Selector()
  public static postCodeInfo(state: DashboardModel) {
    return state.postCodeInfo;
  }

  @Selector()
  public static policyStats(state: DashboardModel) {
    return state.policyStats;
  }

  @Selector()
  public static quicklinkList(state: DashboardModel) {
    let list = state.policyStats.quicklinkList.filter((i) => i.descp != null);
    if (!list || list.length === 0) {
      return [];
    }
    const maxItems = list.length >= 5 ? 5 : list.length;
    return list.slice(0, maxItems);
  }

  @Selector()
  public static landingPageMenu(state: DashboardModel) {
    return state.dashboardMenu;
  }

  @Action(GET_CAROUSEL_CONTENT)
  public getCorouselcontent(
    { patchState }: StateContext<DashboardModel>,
    { payload }: GET_CAROUSEL_CONTENT,
  ) {
    return this.landingPageService.getCarouselContent(payload).pipe(
      map((res) => {
        if (res) {
          patchState({
            carouselContent: res.contents,
          });
        } else {
          patchState({
            carouselContent: null,
          });
        }
      }),
    );
  }

  @Action(GET_SUBMENU_REDIRECTURL)
  public getSubMenuRedirectUrl(
    _ctx: StateContext<DashboardModel>,
    { payload }: GET_SUBMENU_REDIRECTURL,
  ) {
    if (
      payload === "ISSUE" ||
      payload === "REPORTS" ||
      payload === "ENQUIRY" ||
      payload === "SUMMARY" ||
      payload === "CI"
    ) {
      const redirectPayload = {
        operator: this.store.selectSnapshot<string>(
          (state) => state.SatUserState.user.userId,
        ),
        action: payload,
      };
      this.store.dispatch(new GET_REDIRECT_URL(redirectPayload));
    } else {
      return this.landingPageService.getDirectUrlFromSubMenu(payload).pipe(
        map((res) => {
          if (res.redirectUrl) {
            window.location.href = res.redirectUrl;
          }
        }),
      );
    }
  }

  @Action(GET_CHILDSUBMENU_REDIRECTURL)
  public getChildSubMenuRedirectUrl(
    _ctx: StateContext<DashboardModel>,
    { payload }: GET_CHILDSUBMENU_REDIRECTURL,
  ) {
    const redirectPayload = {
      operator: this.store.selectSnapshot<string>(
        (state) => state.SatUserState.user.userId,
      ),
      action: payload,
    };
    this.store.dispatch(new GET_REDIRECT_URL(redirectPayload));
  }

  @Action(GET_ANNOUNCEMENTS)
  public getAnnouncementList(
    { patchState }: StateContext<DashboardModel>,
    _action: GET_ANNOUNCEMENTS,
  ) {
    patchState({ announcementList: { disabledLoading: true } });
    return this.landingPageService.getAnnouncements().pipe(
      map((res) => {
        patchState({
          announcementList: {
            disabledLoading: false,
            data: res ? res : null,
          },
        });
      }),
    );
  }

  @Action(GET_USER_PERFORMANCE)
  public getUserPerformance(
    { patchState }: StateContext<DashboardModel>,
    { payload }: GET_USER_PERFORMANCE,
  ) {
    patchState({ userPerformance: { disabledLoading: true } });
    return this.landingPageService.getUserPerformance(payload).pipe(
      map((res) => {
        patchState({
          userPerformance: {
            disabledLoading: false,
            data: res ? res : null,
          },
        });
      }),
    );
  }

  @Action(GET_TOTAL_OUTSTANDING)
  public getTotalOutstanding(
    { patchState }: StateContext<DashboardModel>,
    { payload }: GET_TOTAL_OUTSTANDING,
  ) {
    // patchState({ totalOutstanding: { disabledLoading: true } })
    return this.landingPageService.getTotalOutstandingPremium(payload).pipe(
      map((res) => {
        patchState({
          totalOutstanding: {
            // disabledLoading: false,
            data: res ? res : null,
          },
        });
      }),
    );
  }

  @Action(GET_OUTSTANDING_CB)
  public getOutstandingCB(
    { patchState }: StateContext<DashboardModel>,
    { payload }: GET_OUTSTANDING_CB,
  ) {
    // patchState({ outstandingCb: { disabledLoading: true } })
    return this.landingPageService.getOutstandingCBcalert(payload).pipe(
      map((res) => {
        patchState({
          outstandingCb: {
            // disabledLoading: false,
            data: res ? res : null,
          },
        });
      }),
    );
  }

  @Action(GET_POLICY_MANAGEMENT)
  public getPolicyManagement(
    { patchState }: StateContext<DashboardModel>,
    { payload }: GET_POLICY_MANAGEMENT,
  ) {
    patchState({ policyManagement: { disabledLoading: true } });
    return this.landingPageService.getPolicyManagement(payload.userId).pipe(
      map((res) => {
        patchState({
          policyManagement: {
            disabledLoading: false,
            data: res ? res : [],
          },
        });
      }),
    );
  }

  @Action(GET_POSTCODE_INFO)
  public getPostCodeInfo(
    { patchState }: StateContext<DashboardModel>,
    { payload }: GET_POSTCODE_INFO,
  ) {
    return this.landingPageService.fetchPostCodeInfo(payload).pipe(
      map((res) => {
        if (res.message) {
          patchState({
            postCodeInfo: {
              code: "",
              descp: "",
              city: "",
              state: "",
            },
          });
        } else {
          patchState({
            postCodeInfo: res.data,
          });
        }
      }),
    );
  }

  @Action(GET_MENU_LIST)
  public getLandingPageMenu(
    { patchState }: StateContext<DashboardModel>,
    { payload }: GET_MENU_LIST,
  ) {
    return this.landingPageService.getLandingPageMenu(payload).pipe(
      map((res) => {
        patchState({
          dashboardMenu: res.menuList,
        });
      }),
    );
  }

  @Action(SEND_QL_DATA)
  public sendQlData(
    { patchState }: StateContext<DashboardModel>,
    { payload }: SEND_QL_DATA,
  ) {
    return this.landingPageService.sendQlData(payload).pipe(
      map((res) => {
        patchState({
          qlData: res.quicklinkList.link,
        });
      }),
    );
  }

  @Action(GET_POLICY_STATS)
  public getPolicyStats(
    { patchState }: StateContext<DashboardModel>,
    { payload }: GET_POLICY_STATS,
  ) {
    return this.landingPageService.getPolicyStats(payload).pipe(
      map((res) => {
        if (res) {
          patchState({
            policyStats: res,
          });
        } else {
          patchState({
            policyStats: null,
          });
        }
      }),
    );
  }

  @Action(RESET_DISABLED_LOADING_STATE)
  public resetDisabledLoadingState({
    patchState,
  }: StateContext<DashboardModel>) {
    patchState({
      userPerformance: {
        disabledLoading: false,
        data: null,
      },
    });
  }

  @Action(SAVE_QUICKLINK)
  public saveQuickLink(
    _ctx: StateContext<DashboardModel>,
    { payload }: SAVE_QUICKLINK,
  ) {
    return this.landingPageService.saveQuickLink(payload);
  }

  @Action(REDIRECT_PROFILE)
  public redirectProfile(
    _ctx: StateContext<DashboardModel>,
    _action: REDIRECT_PROFILE,
  ) {
    return this.router.navigate(["/profile"]);
  }
}
